import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { PlayerProfileScopeSwitcher } from "../components/PlayerFamilyProfileSwitcher";
import { makeLatestOnly } from "../lib/latestOnly";
import { matchScoreFromWinner } from "../lib/scoreFromWinner";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";
import {
  LineCompetitorLines,
  MatchCompetitorLines,
  participantName,
  participantRankLabel,
} from "../components/MatchCompetitorLines";
import PlayerJornadaRetoCard, { jornadaRetoState, jornadaRetoStatusLabel } from "../components/PlayerJornadaRetoCard";
import Icon from "../components/icons";
import PlayerStageLockedCard from "../components/PlayerStageLockedCard";
import PlayerTournamentBottomNav from "../components/PlayerTournamentBottomNav";
import { playerStageLocked } from "../lib/playerStage";
import { groupNameWithDivision, namesInSeveralDivisions } from "../lib/groupLabels";
import TournamentSponsorSlots from "../components/TournamentSponsorSlots";
import { formatDate } from "../lib/dateFormat";
import { isSchedulePendingForPlayer, SCHEDULE_PENDING_LABEL } from "../lib/matchScheduleStatus";
import { playoffStageLabelsByRound } from "../lib/roundRobinPhases";
import { jornadaRangeText, windowForMatch } from "../lib/jornadaDates";
import { restScreenLine, restsForJornada } from "../lib/jornadaRests";
import {
  DROPPED_NAME_SUFFIX,
  dropMatchLabel,
  dropMatchState,
  isCancelledByDrop,
  isDroppedParticipant,
} from "../lib/dropMatch";
import {
  combinedRawLabelDisplay,
  sortRawLabels,
  stageForRawLabels,
} from "../lib/roundLabelAliases";
import {
  api,
  ApiError,
  type JornadaRestEntry,
  type JornadaWindow,
  type PlayerJornadaRetosPayload,
  type PlayerReportableSettings,
  type PlayerStageVisibility,
  type TournamentMatch,
} from "../lib/api";

type GroupKey = string;

interface RoundOption {
  key: string;
  label: string;
  displayLabel: string;
  isDrawRound: boolean;
  number: string;
  sort: number;
}

function formatTime(value: string | null): string {
  if (!value) return "—";
  return value.slice(0, 5);
}

/** Fecha de la celda: con el horario aún sin publicar (Publicar horarios,
 * Eduardo 2026-10-01) el servidor no la manda y aquí dice «Horario por
 * confirmar» en lugar de «—». */
function matchDateCell(match: TournamentMatch): string {
  return isSchedulePendingForPlayer(match) ? SCHEDULE_PENDING_LABEL : formatDate(match.scheduled_date);
}

/** Fechas de la jornada elegida, debajo del selector (sin calendario de
 * canchas; Eduardo 2026-10-05: «de cuándo a cuándo es la jornada 1»), con el
 * texto de «Tu próximo reto»: «del martes 6 al domingo 11 de octubre». Son
 * las de la categoría de tu partido; si esa jornada descansas, las de tu
 * categoría (`myDivisionId`); si no estás en el rol, solo si todas las
 * categorías visibles comparten las mismas fechas. */
export function roundWindowRange(
  matches: TournamentMatch[],
  windows: JornadaWindow[],
  myParticipantId: number | null,
  myDivisionId?: number | null,
): string | null {
  if (windows.length === 0) return null;
  const own = matches.find((match) => matchHasPlayer(match, myParticipantId));
  const myCategory =
    myDivisionId === undefined ? [] : matches.filter((match) => (match.division_id ?? null) === myDivisionId);
  const candidates = own ? [own] : myCategory.length > 0 ? myCategory : matches;
  const ranges = new Set<string>();
  for (const match of candidates) {
    const window = windowForMatch(windows, match);
    const range = window ? jornadaRangeText(window.starts_on, window.ends_on) : null;
    if (range) ranges.add(range);
  }
  return ranges.size === 1 ? Array.from(ranges)[0] : null;
}

/** Categoría del jugador según sus partidos (undefined si aún no está en el rol). */
function ownDivisionId(matches: TournamentMatch[], participantId: number | null): number | null | undefined {
  const own = matches.find((match) => matchHasPlayer(match, participantId));
  return own ? own.division_id ?? null : undefined;
}

function matchHasPlayer(match: TournamentMatch, participantId: number | null): boolean {
  if (!participantId) return false;
  return (
    match.player1_participant_id === participantId ||
    match.player2_participant_id === participantId
  );
}

/** Grupo donde el jugador descansa la jornada `roundKey` («round-3»), para
 * abrirlo y marcarlo como «Tu grupo» aunque esa jornada no tenga partido suyo. */
function restingGroupKey(
  rests: JornadaRestEntry[],
  roundKey: string,
  participantId: number | null,
): GroupKey | null {
  const found = /^round-(\d+)$/.exec(roundKey);
  if (!found || !participantId) return null;
  const rest = restsForJornada(rests, Number(found[1])).find((entry) =>
    entry.participants.some((participant) => participant.id === participantId),
  );
  return rest ? String(rest.group_id) : null;
}

function groupKeyFor(match: TournamentMatch): GroupKey {
  return match.group_id === null ? "none" : String(match.group_id);
}

function groupNameFor(match: TournamentMatch, repeatedNames: ReadonlySet<string>): string {
  const name = match.group_name?.trim();
  if (!name) return "Partidos";
  // «Grupo 1» puede existir en dos categorías (Eduardo 2026-10-01): el
  // jugador inscrito en ambas ve cada sección con su categoría.
  return groupNameWithDivision(name, match.division_name, repeatedNames);
}

function roundNumber(label: string | null): string {
  if (!label) return "—";
  const found = label.match(/(\d+)/);
  return found ? found[1] : label;
}

function roundSortValue(label: string | null): number {
  if (!label) return Number.MAX_SAFE_INTEGER;
  const playoff = playoffRoundMeta(label);
  if (playoff) return playoff.sort;
  const found = label.match(/(\d+)/);
  return found ? Number(found[1]) : Number.MAX_SAFE_INTEGER - 1;
}

function normalizeRoundLabel(label: string | null): string {
  return (label || "Sin jornada")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function playoffRoundMeta(label: string | null): { key: string; displayLabel: string; sort: number } | null {
  const normalized = normalizeRoundLabel(label);
  if (/dieciseis|16\s*avos|ronda\s+de\s+16/.test(normalized)) {
    return { key: "draw-16avos", displayLabel: "16avos", sort: 10000 };
  }
  if (/octavos|8\s*avos|ronda\s+de\s+8/.test(normalized)) {
    return { key: "draw-8avos", displayLabel: "8avos", sort: 10001 };
  }
  if (/cuartos|4\s*tos|ronda\s+de\s+4/.test(normalized)) {
    return { key: "draw-4tos", displayLabel: "4tos", sort: 10002 };
  }
  if (/semifinal/.test(normalized)) {
    return { key: "draw-semifinal", displayLabel: "Semifinal", sort: 10003 };
  }
  if (/tercer\s+lugar|3er\s+lugar|tercero/.test(normalized)) {
    return { key: "draw-tercer-lugar", displayLabel: "Tercer lugar", sort: 10004 };
  }
  if (/^final\b/.test(normalized)) {
    return { key: "draw-final", displayLabel: "Final", sort: 10005 };
  }
  return null;
}

function roundOptionKey(label: string | null): string {
  const playoff = playoffRoundMeta(label);
  if (playoff) return playoff.key;
  const normalized = (label || "Sin jornada").trim();
  const found = normalized.match(/(\d+)/);
  if (found) return `round-${Number(found[1])}`;
  return `label-${normalizeRoundLabel(normalized)}`;
}

function roundOptionDisplayLabel(label: string | null, periodLabel: string): string {
  const playoff = playoffRoundMeta(label);
  if (playoff) return playoff.displayLabel;
  return `${periodLabel} ${roundNumber(label)}`;
}

export function roundOptionLabel(option: RoundOption, _periodLabel: string): string {
  // Ronda de playoffs/draw → etiqueta canónica; en cualquier otro caso se
  // muestra el round_label CRUDO (option.label), no un prefijo reconstruido
  // (un torneo semanal guarda "Semana N" y no debe volverse "Jornada N").
  // Contrato display-only del gate #136.
  return option.isDrawRound ? option.displayLabel : option.label;
}

function compareMatches(a: TournamentMatch, b: TournamentMatch): number {
  const byRound = roundSortValue(a.round_label) - roundSortValue(b.round_label);
  if (byRound !== 0) return byRound;
  const byDate = (a.scheduled_date ?? "9999-99-99").localeCompare(
    b.scheduled_date ?? "9999-99-99",
  );
  if (byDate !== 0) return byDate;
  const byTime = (a.scheduled_time ?? "99:99").localeCompare(b.scheduled_time ?? "99:99");
  if (byTime !== 0) return byTime;
  return (a.match_number ?? a.id) - (b.match_number ?? b.id);
}

function matchStatusShort(match: TournamentMatch): string {
  // Eduardo 2026-10-06: el marcador se lee desde quien ganó.
  if (match.score_text?.trim()) return matchScoreFromWinner(match).trim();
  // Eduardo 2026-10-05 (torneo 69): contra alguien dado de baja, qué pasó
  // con el partido; cancelado por la baja, esa jornada se descansa.
  const drop = dropMatchState(match);
  if (drop) return dropMatchLabel(drop);
  if (match.status === "bye") return "BYE";
  if (match.status === "walkover") return "W/O";
  if (match.status === "double_walkover") return "Doble W.O.";
  if (match.status === "defaulted") return "DEF";
  if (match.status === "retired") return "RET";
  if (match.status === "no_show") return "No-show";
  if (match.status === "cancelled") return "Cancelado";
  return "Pendiente";
}

export type MatchCardTone = "ok" | "pending" | "warn" | "muted" | "live";

/** Píldora de la tarjeta del partido (maqueta 2026-10-06): Jugado / Por
 * jugar / W.O. por baja / Cancelado por baja… */
export function matchCardStatus(match: TournamentMatch): { label: string; tone: MatchCardTone } {
  const drop = dropMatchState(match);
  if (drop) return { label: dropMatchLabel(drop), tone: drop.kind === "cancelled" ? "muted" : "warn" };
  switch (match.status) {
    case "completed":
      return { label: "Jugado", tone: "ok" };
    case "retired":
      return { label: "Jugado · retiro", tone: "ok" };
    case "defaulted":
      // Eduardo 2026-10-13 (DEF): con marcador se jugó hasta el default,
      // como un retiro; sin marcador, como un W.O.
      return /\d/.test(match.score_text ?? "") ? { label: "Jugado · default", tone: "ok" } : { label: "Default", tone: "warn" };
    case "walkover":
    case "no_show":
      return { label: "W.O.", tone: "warn" };
    case "double_walkover":
      return { label: "Doble W.O.", tone: "warn" };
    case "bye":
      return { label: "BYE", tone: "muted" };
    case "cancelled":
      return { label: "Cancelado", tone: "muted" };
    case "in_progress":
      return { label: "En juego", tone: "live" };
    case "postponed":
      return { label: "Pospuesto", tone: "warn" };
    default:
      return match.score_text?.trim() ? { label: "Jugado", tone: "ok" } : { label: "Por jugar", tone: "pending" };
  }
}

function isWalkoverLike(match: TournamentMatch): boolean {
  return ["walkover", "defaulted", "no_show"].includes(match.status);
}

/** Un lado del partido: seed, nombre (con su perfil), ganador en negritas y,
 * en su renglón, el marcador leído desde quien ganó. */
function MatchSide({
  participant,
  winner,
  score,
  bye = false,
}: {
  participant: TournamentMatch["player1"];
  winner: boolean;
  score: string | null;
  bye?: boolean;
}) {
  const rank = participantRankLabel(participant);
  const dropped = !bye && isDroppedParticipant(participant);
  const name = participantName(participant);
  return (
    <div className={`ps-match__side${winner ? " is-winner" : ""}${dropped ? " is-dropped" : ""}`}>
      <span className="ps-match__who">
        <span className={`ps-seed${rank ? "" : " is-empty"}`} aria-hidden={rank ? undefined : true}>
          {rank ?? ""}
        </span>
        {bye ? (
          <strong>BYE</strong>
        ) : participant?.player_profile_id ? (
          <Link className="player-name-action ps-match__name" to={`/player/profiles/${participant.player_profile_id}`}>
            {name}
          </Link>
        ) : (
          <span className="ps-match__name">{name}</span>
        )}
        {dropped ? <small className="ps-match__drop">{DROPPED_NAME_SUFFIX}</small> : null}
      </span>
      {score ? <span className="ps-match__score">{score}</span> : null}
    </div>
  );
}

function isTerminalMatch(match: TournamentMatch): boolean {
  return ["completed", "walkover", "no_show", "defaulted", "retired", "double_walkover", "cancelled"].includes(match.status);
}

function isWeeklyTournament(tournamentName: string, matches: TournamentMatch[]): boolean {
  return (
    /semana|semanal/i.test(tournamentName) ||
    matches.some((match) => /semana/i.test(match.round_label ?? ""))
  );
}

export function makeRoundOptions(matches: TournamentMatch[]): RoundOption[] {
  // Jornadas de playoffs (round_label "Jornada N" + notes de playoffs) →
  // etiqueta canónica de tenis (Preliminar/Cuartos/Semifinal/Final).
  const playoffStages = playoffStageLabelsByRound(matches);
  // TODOS los raw labels por key de opción (los aliases "Semana 4" y
  // "Jornada 4" comparten `round-4`), ordenados de forma determinista: la
  // opción no depende del orden de las filas.
  const rawsByKey = new Map<string, Set<string>>();
  for (const match of matches) {
    const raw = (match.round_label ?? "").trim();
    if (!raw) continue;
    const key = roundOptionKey(match.round_label);
    const bucket = rawsByKey.get(key) ?? new Set<string>();
    bucket.add(raw);
    rawsByKey.set(key, bucket);
  }
  const map = new Map<string, RoundOption>();
  for (const match of matches) {
    const key = roundOptionKey(match.round_label);
    if (map.has(key)) continue;
    const raws = sortRawLabels(rawsByKey.get(key) ?? []);
    // Etapa canónica SOLO si cubre a TODOS los aliases del key; el label es
    // el raw único exacto o el display combinado determinista
    // ("Jornada 4 · Semana 4") — nunca el primer alias silencioso.
    const stage = stageForRawLabels(
      raws.length > 0 ? raws : [(match.round_label ?? "").trim()],
      playoffStages,
    );
    const representative = raws[0] ?? match.round_label;
    const label = raws.length > 0
      ? (raws.length === 1 ? raws[0] : combinedRawLabelDisplay(raws))
      : (match.round_label || "Sin jornada");
    map.set(key, {
      key,
      label,
      displayLabel: stage ?? roundOptionDisplayLabel(representative, "Jornada"),
      isDrawRound: Boolean(stage) || Boolean(playoffRoundMeta(representative)),
      number: roundNumber(representative),
      sort: roundSortValue(representative),
    });
  }
  return Array.from(map.values()).sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label, "es", { numeric: true }));
}

function latestRoundKey(matches: TournamentMatch[]): string {
  const sorted = [...matches].sort(compareMatches);
  const latest = [...sorted].reverse().find((match) => match.round_label);
  return roundOptionKey(latest?.round_label || sorted[0]?.round_label || null);
}

function localTodayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

/**
 * "Jornada actual" por calendario: la ronda cuyas fechas programadas incluyen
 * hoy o la siguiente por venir; si todas quedaron atrás, la última con fechas.
 * Devuelve null cuando ningún partido tiene fecha (torneos sin calendario).
 */
function currentRoundKeyByDate(matches: TournamentMatch[]): string | null {
  const today = localTodayIso();
  const rounds = new Map<string, { sort: number; maxDate: string }>();
  for (const match of matches) {
    if (!match.scheduled_date || !match.round_label) continue;
    const key = roundOptionKey(match.round_label);
    const existing = rounds.get(key);
    if (existing) {
      if (match.scheduled_date > existing.maxDate) existing.maxDate = match.scheduled_date;
    } else {
      rounds.set(key, { sort: roundSortValue(match.round_label), maxDate: match.scheduled_date });
    }
  }
  if (rounds.size === 0) return null;
  const ordered = [...rounds.entries()].sort((a, b) => a[1].sort - b[1].sort);
  const current = ordered.find(([, round]) => round.maxDate >= today);
  return (current ?? ordered[ordered.length - 1])[0];
}

function defaultRoundKey(
  matches: TournamentMatch[],
  participantId: number | null,
  preferLatest = false,
  currentJornada: number | null = null,
): string {
  if (preferLatest) return latestRoundKey(matches);
  // Sin calendario de canchas, la jornada actual de tu categoría que manda el
  // servidor (Eduardo 2026-10-05: la misma regla de «Descansas esta jornada»
  // y de Cerrar torneo). Antes abría en tu primer partido sin resultado: si
  // descansabas la Jornada 1, en la 2.
  if (currentJornada !== null) {
    const key = `round-${currentJornada}`;
    if (matches.some((match) => roundOptionKey(match.round_label) === key)) return key;
  }
  // La jornada actual la dicta el calendario del torneo, no el primer partido
  // sin resultado (en escaleras pre-generadas eso abría jornadas lejanas).
  const byDate = currentRoundKeyByDate(matches);
  if (byDate) return byDate;
  const sorted = [...matches].sort(compareMatches);
  const ownCurrent = sorted.find((match) => matchHasPlayer(match, participantId) && !isTerminalMatch(match));
  if (ownCurrent?.round_label) return roundOptionKey(ownCurrent.round_label);
  const current = sorted.find((match) => !isTerminalMatch(match));
  if (current?.round_label) return roundOptionKey(current.round_label);
  return latestRoundKey(sorted);
}

/** Lo del perfil elegido en este torneo; `key` es «torneo:perfil» de quien lo pidió. */
interface PlayerGamesData {
  key: string;
  matches: TournamentMatch[];
  myParticipantId: number | null;
  roundWindows: JornadaWindow[];
  restByRound: JornadaRestEntry[];
}

const NO_MATCHES: TournamentMatch[] = [];
const NO_WINDOWS: JornadaWindow[] = [];
const NO_RESTS: JornadaRestEntry[] = [];

interface PlayerTournamentGamesPageProps {
  viewMode?: "calendar" | "matchups" | "results";
}

export default function PlayerTournamentGamesPage({
  viewMode = "calendar",
}: PlayerTournamentGamesPageProps = {}) {
  const { id } = useParams();
  const tournamentId = Number(id);
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tournament, setTournament] = useState<{ id: number; name: string } | null>(null);
  const [visibility, setVisibility] = useState<PlayerStageVisibility | null>(null);
  const [settings, setSettings] = useState<PlayerReportableSettings | null>(null);
  // Lo que depende del perfil lleva la clave del torneo y perfil que lo PIDIÓ
  // (igual que jornadaRetos): al cambiar de hermano, los partidos y «Tu
  // partido» del anterior no se ven ni un render mientras llegan los nuevos.
  const [playerData, setPlayerData] = useState<PlayerGamesData | null>(null);
  // Tu partido de cada jornada (mismo endpoint que el reto del inicio del
  // torneo: rival, celular, tu reporte). Solo round robin sin calendario de
  // canchas; en lo demás el servidor manda la lista vacía.
  // Llevan la clave del perfil y torneo que los PIDIÓ (bloqueo de Torni,
  // PR #581): dos perfiles de la misma cuenta pueden compartir la pareja
  // (mismo participant_id) y el remitente del WhatsApp es de cada perfil.
  const [jornadaRetos, setJornadaRetos] = useState<{ key: string; payload: PlayerJornadaRetosPayload } | null>(null);
  const [selectedRoundKey, setSelectedRoundKey] = useState<string>("");
  const [openGroupKeys, setOpenGroupKeys] = useState<Set<GroupKey>>(new Set());
  // Cuenta familiar (Eduardo 2026-09-29): «Tú», tu grupo y tu partido son
  // los del perfil elegido para este torneo.
  const scope = usePlayerProfileScope({ tournamentId });
  const scopeProfileId = scope.profileId;
  const latestLoad = useMemo(() => makeLatestOnly(), []);

  const scopeKey = `${tournamentId}:${scopeProfileId ?? "self"}`;

  const load = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId) || !scope.ready) return;
    const attempt = latestLoad.begin();
    setLoading(true);
    setError(null);
    try {
      const response = await api.resultReports.listTournamentResultsForPlayer(token, tournamentId, scopeProfileId);
      if (!latestLoad.isCurrent(attempt)) return;
      const payload = response.data;
      const defaultRound = defaultRoundKey(
        payload.matches,
        payload.my_participant_id,
        viewMode === "matchups",
        payload.current_jornada ?? null,
      );
      const visibleMatches = payload.matches.filter((match) => roundOptionKey(match.round_label) === defaultRound);
      const ownMatch = visibleMatches.find((match) => matchHasPlayer(match, payload.my_participant_id));
      const fallbackMatch = visibleMatches[0] ?? payload.matches[0];

      setTournament(payload.tournament);
      setVisibility(payload.player_visibility ?? null);
      setSettings(payload.settings ?? null);
      setPlayerData({
        key: scopeKey,
        matches: payload.matches,
        myParticipantId: payload.my_participant_id,
        roundWindows: payload.round_windows ?? [],
        // Quién descansa cada jornada, por grupo (Eduardo 2026-10-05).
        restByRound: payload.rest_by_round ?? [],
      });
      setSelectedRoundKey(defaultRound);
      const restingKey = restingGroupKey(payload.rest_by_round ?? [], defaultRound, payload.my_participant_id);
      setOpenGroupKeys(new Set([ownMatch ? groupKeyFor(ownMatch) : restingKey ?? (fallbackMatch ? groupKeyFor(fallbackMatch) : "none")]));
    } catch (err) {
      if (!latestLoad.isCurrent(attempt)) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar los juegos del torneo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (latestLoad.isCurrent(attempt)) setLoading(false);
    }
  }, [latestLoad, scope.ready, scopeKey, scopeProfileId, token, tournamentId, viewMode]);

  useEffect(() => {
    void load();
  }, [load]);

  const stageLockedNow = playerStageLocked(visibility);
  useEffect(() => {
    // Otro perfil u otro torneo: nunca se queda el reto (ni quien escribe el
    // WhatsApp) del anterior mientras llega el nuevo.
    setJornadaRetos(null);
    if (!token || Number.isNaN(tournamentId) || !scope.ready || stageLockedNow) return;
    let cancelled = false;
    const requestedKey = scopeKey;
    api.standings
      .jornadaRetosForPlayer(token, tournamentId, scopeProfileId)
      .then((res) => {
        if (!cancelled) setJornadaRetos({ key: requestedKey, payload: res.data });
      })
      .catch(() => {
        if (!cancelled) setJornadaRetos(null);
      });
    return () => {
      cancelled = true;
    };
  }, [scope.ready, scopeKey, scopeProfileId, stageLockedNow, token, tournamentId]);

  // Solo los datos de ESTE perfil y torneo; los de otro no se usan.
  const currentData = playerData?.key === scopeKey ? playerData : null;
  const matches = currentData?.matches ?? NO_MATCHES;
  const myParticipantId = currentData?.myParticipantId ?? null;
  const roundWindows = currentData?.roundWindows ?? NO_WINDOWS;
  const restByRound = currentData?.restByRound ?? NO_RESTS;

  const roundOptions = useMemo(() => makeRoundOptions(matches), [matches]);

  const visibleMatches = useMemo(() => {
    const key = selectedRoundKey || roundOptions[0]?.key || "";
    return matches.filter((match) => roundOptionKey(match.round_label) === key);
  }, [matches, roundOptions, selectedRoundKey]);

  const grouped = useMemo(() => {
    const repeatedNames = namesInSeveralDivisions(
      visibleMatches
        .filter((match) => match.group_id !== null)
        .map((match) => ({ name: match.group_name, divisionKey: match.division_id })),
    );
    const map = new Map<GroupKey, { key: GroupKey; name: string; matches: TournamentMatch[] }>();
    for (const match of [...visibleMatches].sort(compareMatches)) {
      const key = groupKeyFor(match);
      const existing = map.get(key);
      if (existing) {
        existing.matches.push(match);
      } else {
        map.set(key, { key, name: groupNameFor(match, repeatedNames), matches: [match] });
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.key === "none") return 1;
      if (b.key === "none") return -1;
      return a.name.localeCompare(b.name, "es", { numeric: true });
    });
  }, [visibleMatches]);

  if (Number.isNaN(tournamentId)) {
    return <Navigate to="/player/tournaments" replace />;
  }

  const usesCalendar = settings?.uses_court_calendar ?? true;
  const weekly = isWeeklyTournament(tournament?.name ?? "", matches);
  const pageTitle =
    viewMode === "matchups"
      ? "Enfrentamientos"
      : viewMode === "results"
        ? "Draw"
        : usesCalendar
          ? weekly
            ? "Rol Semanal"
            : "Calendario"
          : weekly
            ? "Rol Semanal"
            : "Juegos";
  const periodLabel = weekly ? "Semana" : "Jornada";
  const currentRound = roundOptions.find((option) => option.key === selectedRoundKey) ?? roundOptions[0];
  // Número de la jornada elegida («round-3» → 3); los playoffs no tienen descansos.
  const currentRoundNumber = (() => {
    const found = /^round-(\d+)$/.exec(currentRound?.key ?? "");
    return found ? Number(found[1]) : null;
  })();
  const windowRange =
    !usesCalendar && !playerStageLocked(visibility) && currentRound
      ? roundWindowRange(visibleMatches, roundWindows, myParticipantId, ownDivisionId(matches, myParticipantId))
      : null;

  function toggleGroup(key: GroupKey, open: boolean) {
    setOpenGroupKeys((prev) => {
      const next = new Set(prev);
      if (open) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  function handleRoundChange(nextRoundKey: string) {
    setSelectedRoundKey(nextRoundKey);
    const nextMatches = matches.filter((match) => roundOptionKey(match.round_label) === nextRoundKey);
    const ownMatch = nextMatches.find((match) => matchHasPlayer(match, myParticipantId));
    const fallbackMatch = nextMatches[0];
    const restingKey = restingGroupKey(restByRound, nextRoundKey, myParticipantId);
    setOpenGroupKeys(new Set([ownMatch ? groupKeyFor(ownMatch) : restingKey ?? (fallbackMatch ? groupKeyFor(fallbackMatch) : "none")]));
  }

  // Tu partido de la jornada elegida (mismo dato que el reto del inicio).
  // Solo la respuesta pedida para ESTE perfil y torneo (aunque otro perfil de
  // la cuenta comparta la pareja); la de otro perfil no se usa ni un render.
  const ownRetos = jornadaRetos?.key === scopeKey ? jornadaRetos.payload : null;
  const jornadaEntries =
    ownRetos?.participant_id != null && ownRetos.participant_id === myParticipantId
      ? ownRetos.jornadas
      : [];
  const retoEntry =
    currentRoundNumber != null
      ? jornadaEntries.find((entry) => entry.round_number === currentRoundNumber) ?? null
      : null;
  // Quien escribe el WhatsApp: el perfil propio elegido (nombre y apellido
  // del servidor), nunca la etiqueta de la pareja («Lucia / Beto»).
  const retoSender = jornadaEntries.length ? ownRetos?.sender ?? null : null;
  // Encabezado «CATEGORÍA 1 · GRUPO A1» desde tu partido (o el torneo).
  const ownAnyMatch = matches.find((match) => matchHasPlayer(match, myParticipantId)) ?? null;
  const heroEyebrow =
    [ownAnyMatch?.division_name?.trim(), ownAnyMatch?.group_name?.trim()].filter(Boolean).join(" · ") ||
    tournament?.name ||
    "Torneo";
  const stageLocked = playerStageLocked(visibility);

  function renderRetoCard() {
    if (!retoEntry || stageLocked) return null;
    if (retoEntry.rest || !retoEntry.match) {
      return (
        <section className="pt-home__rest ps-rest" aria-labelledby="ps-games-rest-title">
          <div className="pt-home__rest-top">
            <div className="pt-home__reto-head">
              <span className="pt-home__eyebrow pt-home__eyebrow--rest">
                <Icon name="moon" />
                Esta jornada
              </span>
              <span className="pt-home__pill pt-home__pill--rest">{retoEntry.round_label}</span>
            </div>
            <h2 id="ps-games-rest-title" className="pt-home__rest-title">
              Descansas
            </h2>
            <p className="pt-home__rest-copy" data-no-toast>
              No tienes partido en esta jornada. En los grupos con número impar de jugadores, cada jornada descansa uno.
            </p>
          </div>
        </section>
      );
    }
    const state = jornadaRetoState(retoEntry, myParticipantId);
    return (
      <PlayerJornadaRetoCard
        entry={retoEntry}
        ownParticipantId={myParticipantId}
        sender={retoSender}
        playerProfileId={scopeProfileId}
        tournamentName={tournament?.name}
        headingId="ps-games-reto-title"
        eyebrow="Tu partido"
        pill={jornadaRetoStatusLabel(state)}
        showDates={false}
        className="ps-reto"
      />
    );
  }

  function renderMatchCard(match: TournamentMatch, idx: number) {
    const isMine = matchHasPlayer(match, myParticipantId);
    const isTie = match.match_kind === "tie";
    const cancelledByDrop = isCancelledByDrop(match);
    const status = matchCardStatus(match);
    const winner1 = match.winner_participant_id != null && match.winner_participant_id === match.player1_participant_id;
    const winner2 = match.winner_participant_id != null && match.winner_participant_id === match.player2_participant_id;
    // Eduardo 2026-10-06: el marcador se lee desde quien ganó y va en su renglón.
    const score = match.score_text?.trim() ? matchScoreFromWinner(match).trim() : null;
    const woText =
      !score && isWalkoverLike(match) && (winner1 || winner2)
        ? match.status === "defaulted" ? "gana por default" : "gana por W.O."
        : null;
    const winnerScore = score ?? woText;
    const cardClass = ["ps-match", isMine && !cancelledByDrop ? "is-mine" : "", cancelledByDrop ? "is-drop" : ""]
      .filter(Boolean)
      .join(" ");
    return (
      <article key={match.id} className={cardClass} data-match-id={match.id} data-drop-match={dropMatchState(match)?.kind}>
        <div className="ps-match__head">
          <span className="ps-match__num">
            {isTie ? `Serie ${match.match_number ?? idx + 1}` : `Juego ${match.match_number ?? idx + 1}`}
            {isMine && !cancelledByDrop ? <span className="ps-match__mine">{isTie ? "Tu serie" : "Tu partido"}</span> : null}
          </span>
          <span className={`ps-chip ps-chip--${status.tone}`}>{status.label}</span>
        </div>
        {isTie ? (
          <>
            <MatchCompetitorLines
              match={match}
              hrefFor={(participant) =>
                participant.player_profile_id ? `/player/profiles/${participant.player_profile_id}` : null
              }
            />
            <small className="ps-match__series">Serie {matchScoreFromWinner(match).trim() || "0-0"}</small>
            {/* Serie por equipos (Mini Copa 2026): sus líneas con quién juega
                cada single/dobles y su marcador. */}
            {(match.lines ?? []).map((line, lineIdx) => (
              <div key={`line-${line.id}`} className="ps-match__line">
                <span className="results-line__kind">
                  L{lineIdx + 1} · {line.line_kind === "doubles" ? "Dobles" : line.line_kind === "mixed" ? "Mixto" : "Singles"}
                </span>
                <LineCompetitorLines line={line} hrefForProfile={(profileId) => `/player/profiles/${profileId}`} />
                <small>{matchStatusShort(line)}</small>
                {usesCalendar ? (
                  <div className="ps-match__meta">
                    <span data-label="Fecha">{matchDateCell(line)}</span>
                    <span data-label="Hora">{formatTime(line.scheduled_time)}</span>
                    <span data-label="Cancha">{line.court ?? "—"}</span>
                  </div>
                ) : null}
              </div>
            ))}
          </>
        ) : (
          <div className="ps-match__sides">
            <MatchSide
              participant={match.player1}
              winner={winner1}
              score={winner1 || (!winner2 && score) ? winnerScore : null}
            />
            <MatchSide
              participant={match.player2}
              winner={winner2}
              score={winner2 ? winnerScore : null}
              bye={match.status === "bye"}
            />
          </div>
        )}
        {usesCalendar ? (
          <div className="ps-match__meta">
            <span data-label="Fecha">{matchDateCell(match)}</span>
            <span data-label="Hora">{formatTime(match.scheduled_time)}</span>
            <span data-label="Cancha">{match.court ?? "—"}</span>
          </div>
        ) : null}
      </article>
    );
  }

  return (
    <div className="pt-reto-theme ps-page">
      {/* Secciones del torneo (Eduardo 2026-10-05): en computadora, pestañas
          aquí arriba; en celular, la barra flotante de abajo. */}
      <PlayerTournamentBottomNav tournamentId={tournamentId} scheduleLabel={usesCalendar ? undefined : "Juegos"} />
      <PlayerProfileScopeSwitcher scope={scope} tournament />
      <header className="ps-head">
        <span className="ps-head__eyebrow">{heroEyebrow}</span>
        <h1>{pageTitle}</h1>
      </header>

      {roundOptions.length > 0 && !stageLocked ? (
        <section className="ps-jornadas" aria-label={`Elige la ${periodLabel.toLowerCase()}`}>
          <div className="ps-jornadas__top">
            <span className="ps-jornadas__label">{periodLabel}</span>
            {/* Eduardo 2026-10-05: sin calendario de canchas, de cuándo a
                cuándo es la jornada elegida; sin fechas, cuál se muestra. */}
            {windowRange ? (
              <span className="ps-jornadas__range" data-no-toast data-jornada-window>
                {windowRange}
              </span>
            ) : currentRound ? (
              <span className="ps-jornadas__range">
                Mostrando {roundOptionLabel(currentRound, periodLabel).toLowerCase()}
              </span>
            ) : null}
          </div>
          <div className="pt-home__jornadas-chips ps-jornadas__chips" role="group" aria-label={periodLabel}>
            {roundOptions.map((option) => {
              const active = option.key === (currentRound?.key ?? "");
              return (
                <button
                  key={option.key}
                  type="button"
                  className={`pt-home__jornada-chip${active ? " is-active" : ""}`}
                  aria-pressed={active}
                  aria-label={roundOptionLabel(option, periodLabel)}
                  data-round-key={option.key}
                  onClick={() => handleRoundChange(option.key)}
                >
                  {option.isDrawRound ? option.displayLabel : option.number}
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      {error && <div className="alert alert--error">{error}</div>}

      {stageLocked ? (
        <PlayerStageLockedCard tournamentId={tournamentId} section="schedule" />
      ) : loading || (!currentData && !error) ? (
        <section className="card">
          <p className="muted">Cargando juegos…</p>
        </section>
      ) : (
        <>
          {renderRetoCard()}
          {grouped.length === 0 ? (
            <section className="card">
              <p className="muted">Todavía no hay juegos publicados para esta {periodLabel.toLowerCase()}.</p>
            </section>
          ) : (
            <section className="ps-groups player-games-list" aria-label={pageTitle}>
              {grouped.map((group) => {
                const ownGroup =
                  group.matches.some((match) => matchHasPlayer(match, myParticipantId)) ||
                  restingGroupKey(restByRound, currentRound?.key ?? "", myParticipantId) === group.key;
                const open = openGroupKeys.has(group.key);
                const counted = group.matches.filter(
                  (match) => !isCancelledByDrop(match) && match.status !== "cancelled",
                );
                const played = counted.filter(isTerminalMatch).length;
                const bodyId = `ps-games-group-${group.key}`;
                return (
                  <section
                    key={group.key}
                    className={`ps-group player-games-group${ownGroup ? " is-own is-player-group" : ""}${open ? " is-open" : ""}`}
                    data-group-key={group.key}
                  >
                    <button
                      type="button"
                      className="ps-group__toggle"
                      aria-expanded={open}
                      aria-controls={open ? bodyId : undefined}
                      onClick={() => toggleGroup(group.key, !open)}
                    >
                      <span className="ps-group__title">
                        {group.name}
                        {ownGroup && <span className="ps-tag">Tu grupo</span>}
                      </span>
                      <span className="ps-group__count">
                        {played} de {counted.length} jugados
                        <Icon name="chevron-down" className="ps-group__chevron" />
                      </span>
                    </button>
                    {open ? (
                      <div id={bodyId} className="ps-group__body">
                        {group.matches.map((match, idx) => renderMatchCard(match, idx))}
                        {/* Eduardo 2026-10-05: quién descansa esta jornada en el grupo
                            («para que no se pierdan»); el jugador que descansa se lee «(tú)». */}
                        {group.key !== "none"
                          ? restsForJornada(restByRound, currentRoundNumber, new Set([Number(group.key)])).map(
                              (rest) => (
                                <p
                                  key={rest.group_id}
                                  className="ps-rest-line schedule-rest-line"
                                  data-no-toast
                                  data-jornada-rest={rest.group_id}
                                >
                                  {restScreenLine(rest, { meId: myParticipantId })}
                                </p>
                              ),
                            )
                          : null}
                      </div>
                    ) : null}
                  </section>
                );
              })}
            </section>
          )}
        </>
      )}

      <TournamentSponsorSlots />
    </div>
  );
}
